import type { ListBuilderConfiguration } from '../types';

/**
 * PD-14688 — PDF Templates settings list. Rows come pre-flattened from
 * PdfTemplateQueryService (companies joined to a label, document type
 * humanized) — the list is small and client-shaped, so the queryFn does
 * the shaping rather than the server.
 */
export const PDF_TEMPLATE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-pdf-templates-all',
			cacheTTL: 60000,
			name: 'All Templates',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'template-name',
					fields: [
						{
							id: 'name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'pdf_template',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Name',
						},
					],
					minWidth: 220,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: 'pdf_template',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'document-type',
					fields: [
						{
							id: 'document-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'documentTypeLabel',
									objectTable: 'pdf_template',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Document Type',
						},
					],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'documentTypeLabel',
							objectTable: 'pdf_template',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'companies',
					fields: [
						{
							id: 'companies-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'companiesLabel',
									objectTable: 'pdf_template',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Assigned Companies',
						},
					],
					minWidth: 180,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'default-flag',
					fields: [
						{
							id: 'default-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isDefault',
									objectTable: 'pdf_template',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: [
									{
										value: true,
										label: 'Default',
										color: { type: 'info' },
									},
									{
										value: false,
										label: '—',
										color: { type: 'neutral' },
									},
								],
							},
							displayStyle: 'badge',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Default',
						},
					],
					sortable: true,
					width: 100,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'isDefault',
							objectTable: 'pdf_template',
						},
					],
				},
				{
					id: 'status',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isActive',
									objectTable: 'pdf_template',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: [
									{
										value: true,
										label: 'Active',
										color: { type: 'success' },
									},
									{
										value: false,
										label: 'Inactive',
										color: { type: 'neutral' },
									},
								],
							},
							displayStyle: 'badge',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Status',
						},
					],
					sortable: true,
					width: 100,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'isActive',
							objectTable: 'pdf_template',
						},
					],
				},
				{
					id: 'modified-date',
					fields: [
						{
							id: 'modified-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'modifiedDate',
									objectTable: 'pdf_template',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
							displayConfig: {
								dateFormat: 'MMM d, yyyy',
							},
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Last Modified',
						},
					],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'modifiedDate',
							objectTable: 'pdf_template',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					headers: [
						{
							id: 'actions-header',
							text: '',
						},
					],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'pen',
									callbackName: 'editTemplate',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'settings:pdf_templates:read',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteTemplate',
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'settings:pdf_templates:manage',
								},
							],
						},
					],
					sortable: false,
					width: 80,
					align: 'center',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/settings/pdf-templates/:id',
			objectLabel: 'PDF Template',
			objectTable: 'pdf_template',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'documentTypeLabel', fieldLabel: 'Document Type' },
				{ fieldName: 'companiesLabel', fieldLabel: 'Assigned Companies' },
				{ fieldName: 'isDefault', fieldLabel: 'Default' },
				{ fieldName: 'isActive', fieldLabel: 'Status' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Last Modified' },
			],
		},
	],
};
